import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

// Court edit form

export default function CourtEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [name, setName] = useState("");
  const [courtTypeId, setCourtTypeId] = useState("");
  const [courtTypes, setCourtTypes] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    Promise.all([api.getCourt(id), api.getCourtTypes()])
      .then(([courtRes, typesRes]) => {
        setName(courtRes.court.name);
        setCourtTypeId(courtRes.court.court_type_id);
        setCourtTypes(typesRes.court_types);
      })
      .catch((e) =>
        setError(
          e instanceof api.ApiError ? e.message : "Failed to load court",
        ),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setError(null);
    setSubmitting(true);

    try {
      await api.updateCourt(id, { name, court_type_id: courtTypeId });
      navigate("/courts");
    } catch (err) {
      setError(
        err instanceof api.ApiError ? err.message : "Failed to update court",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const selectedTypeName = courtTypes.find((ct) => ct.id === courtTypeId)?.name ?? "";

  if (loading) {
    return <p className="text-muted-foreground" data-page="court-edit" data-state="loading">Loading...</p>;
  }

  return (
    <div className="flex flex-col gap-6" data-page="court-edit" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">{selectedTypeName ? `Edit ${selectedTypeName}` : "Edit Facility"}</h1>
        <p className="text-muted-foreground">Update facility details</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">{selectedTypeName ? `${selectedTypeName} Details` : "Facility Details"}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="court-edit">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="court-type">Facility Type</Label>
              <select
                id="court-type"
                data-field="court-type"
                value={courtTypeId}
                onChange={(e) => setCourtTypeId(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              >
                {courtTypes.map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">{selectedTypeName || "Facility"} Name</Label>
              <Input
                id="name"
                data-field="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
              <Link
                to="/courts"
                className={buttonVariants({ variant: "outline" })}
                data-action="cancel"
              >
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
